import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select, Textarea } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { callDisputeBooking } from '@/lib/callables';
import { uploadReportEvidence } from '@/lib/uploadEvidence';
import { useAuth } from '@/stores/auth';
import { REPORT_REASONS, type ReportReason } from '@makhe/shared';

interface Props {
  open: boolean;
  onClose: () => void;
  bookingId: string;
}

export function DisputeModal({ open, onClose, bookingId }: Props) {
  const { firebaseUser } = useAuth();
  const [description, setDescription] = useState('');
  const [reportReason, setReportReason] = useState<ReportReason>('other');
  const [evidenceFiles, setEvidenceFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const reason = description.trim();
    if (reason.length < 10) {
      return toast.error('Tell us what went wrong — at least a sentence.');
    }
    if (!firebaseUser) return toast.error('Sign in to file a dispute.');
    setBusy(true);
    try {
      const evidenceURLs =
        evidenceFiles.length > 0
          ? await uploadReportEvidence(evidenceFiles, firebaseUser.uid)
          : undefined;
      await callDisputeBooking({ bookingId, reason, reportReason, evidenceURLs });
      toast.success('Dispute filed. Our team will reach out.');
      setDescription('');
      setReportReason('other');
      setEvidenceFiles([]);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Something went wrong">
      <div className="space-y-3">
        <p className="text-sm text-makhe-ink/70">
          Describe the issue so we can review it. Payment stays on hold until this is resolved.
        </p>
        <Select
          label="What kind of issue?"
          value={reportReason}
          onChange={(e) => setReportReason(e.target.value as ReportReason)}
          options={REPORT_REASONS.map((r) => ({ value: r, label: r.replace(/_/g, ' ') }))}
        />
        <Textarea
          label="What happened?"
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="The service wasn’t completed as agreed…"
        />
        <label className="block text-sm text-makhe-ink/80">
          Evidence (optional)
          <input
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={(e) => setEvidenceFiles(Array.from(e.target.files ?? []))}
            className="mt-2 block w-full text-sm text-makhe-ink/70 file:mr-3 file:rounded-full file:border-0 file:bg-makhe-line/50 file:px-4 file:py-2 file:text-makhe-ink"
          />
        </label>
        <Button onClick={submit} loading={busy} variant="danger" className="w-full">
          File dispute
        </Button>
      </div>
    </Modal>
  );
}
